import { useState, useReducer, useEffect, useCallback, useRef, useMemo } from 'react'
import { useSearchParams } from 'react-router-dom'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { useToast } from '@/hooks/use-toast'
import {
  Save,
  Play,
  Download,
  FileDown,
  Loader2,
  RotateCcw,
  Check,
  BarChart3,
  PieChart,
  LineChart as LineChartIcon,
  Table2,
  TrendingUp,
  Database,
  ArrowRight,
} from 'lucide-react'
import {
  useExecuteAdHocReport,
  useCreateCustomReport,
  useUpdateCustomReport,
  useCustomReport,
  useReportFields,
  useExportReportPdf,
} from '@/hooks/use-api'
import { exportToCSV } from '@/utils/csvExport'
import {
  TemplateGallery,
  EntitySelector,
  FieldPalette,
  FilterBuilder,
  GroupingConfig,
  ReportPreview,
  reportReducer,
  POPULAR_FIELDS,
  DATE_RANGE_PRESETS,
  REPORT_TEMPLATES,
  FEATURED_TEMPLATE_IDS,
  formatDate,
} from '@/components/reports'
import type {
  ReportConfig,
  ReportField,
  AggregationType,
  ChartType,
  ReportTemplate,
} from '@/components/reports'
import { explainError } from '@/lib/explain-error'

const STORAGE_KEY_LAST_DATA_SOURCE = 'reportBuilder_lastDataSource'

const getInitialDataSource = () => {
  if (typeof window !== 'undefined') {
    return localStorage.getItem(STORAGE_KEY_LAST_DATA_SOURCE) || ''
  }
  return ''
}

// Resolve a ?template= id to its template definition (null when absent/unknown).
function getUrlTemplate(templateId: string | null): ReportTemplate | null {
  if (!templateId) return null
  return REPORT_TEMPLATES.find((t: ReportTemplate) => t.id === templateId) || null
}

// Build the reducer's initial config — seeded from a URL template if present,
// otherwise an empty report defaulting to the last-used data source.
function buildInitialConfig(urlTemplate: ReportTemplate | null): ReportConfig {
  return urlTemplate
    ? {
        name: urlTemplate.name,
        dataSource: urlTemplate.dataSource,
        fields: [],
        filters:
          urlTemplate.filters?.map((f, i) => ({
            id: `filter-${Date.now()}-${i}`,
            field: f.field,
            operator: f.operator,
            value: f.value,
          })) || [],
        groupBy: urlTemplate.groupBy,
        dateGrouping: urlTemplate.dateGrouping,
        sortOrder: 'asc',
        chartType: urlTemplate.chartType,
        limit: 10000,
      }
    : {
        name: '',
        dataSource: getInitialDataSource(),
        fields: [],
        filters: [],
        sortOrder: 'asc',
        chartType: 'table',
        limit: 10000,
      }
}

export default function ReportBuilder() {
  const { toast } = useToast()
  const executeReport = useExecuteAdHocReport()
  const createReport = useCreateCustomReport()
  const updateReport = useUpdateCustomReport()
  const exportPdf = useExportReportPdf()

  // Read URL params reactively (NOT at module scope) so opening a saved report
  // after the module has already loaded — e.g. first creating, then editing —
  // is picked up instead of using a stale baked-in value.
  const [searchParams] = useSearchParams()
  const urlReportId = searchParams.get('id')
  const urlTemplate = useMemo(() => getUrlTemplate(searchParams.get('template')), [searchParams])

  // Saved-report edit mode: when ?id= is present we load that report and switch
  // Save → Update. editingReportId is also set after a first save so repeated
  // saves update the same report instead of creating duplicates.
  const { data: loadedReport } = useCustomReport(urlReportId || '')
  const [editingReportId, setEditingReportId] = useState<string | null>(urlReportId)
  const reportConfigApplied = useRef(false)
  const pendingReportFields = useRef<{
    dataSource: string
    fields: Array<string | { name: string; aggregation?: AggregationType }>
  } | null>(null)

  // Core state via reducer (lazy init reads the URL template once at mount)
  const [config, dispatch] = useReducer(reportReducer, urlTemplate, buildInitialConfig)

  // UI state
  const [reportResults, setReportResults] = useState<any>(null)
  const [selectedTemplate, setSelectedTemplate] = useState<string | null>(urlTemplate?.id || null)
  const [templateGalleryOpen, setTemplateGalleryOpen] = useState(false)
  const [fieldSearch, setFieldSearch] = useState('')
  const [activeQuickFilters, setActiveQuickFilters] = useState<string[]>([])
  const [isLivePreviewEnabled, setIsLivePreviewEnabled] = useState(true)

  // Date filtering state
  const [selectedDateRange, setSelectedDateRange] = useState<string>('')
  const [currentDateField, setCurrentDateField] = useState<string>(urlTemplate?.dateField || '')
  const [customStartDate, setCustomStartDate] = useState('')
  const [customEndDate, setCustomEndDate] = useState('')

  // Period comparison state
  const [comparisonEnabled, setComparisonEnabled] = useState(false)
  const [comparisonPeriod, setComparisonPeriod] = useState<'previous' | 'year_ago'>('previous')
  const [comparisonResults, setComparisonResults] = useState<any>(null)

  // Track if URL template has been fully applied
  const urlTemplateApplied = useRef(false)

  // Welcome state — show builder form when user chooses "Build from scratch", has a saved data source, or has a URL template
  const [showBuilderForm, setShowBuilderForm] = useState(
    !!urlTemplate || !!urlReportId || !!getInitialDataSource()
  )

  // Collapsible section states
  const [dataSourceOpen, setDataSourceOpen] = useState(true)
  const [fieldsOpen, setFieldsOpen] = useState(true)
  const [filtersOpen, setFiltersOpen] = useState(false)
  const [visualizationOpen, setVisualizationOpen] = useState(false)

  // Refs for one-time auto-open behaviors
  const filtersAutoOpened = useRef(false)
  const templateAutoRunTriggered = useRef(false)

  // Fetch fields from backend
  const { data: fieldDefinitions, isLoading: fieldsLoading } = useReportFields(config.dataSource)

  const selectedFieldsCount = config.fields.filter((f) => f.selected).length
  const hasAggregations = config.fields.some((f) => f.selected && f.aggregation !== 'none')

  // Save data source to localStorage
  useEffect(() => {
    if (config.dataSource) {
      localStorage.setItem(STORAGE_KEY_LAST_DATA_SOURCE, config.dataSource)
    }
  }, [config.dataSource])

  // Update fields when data source changes
  useEffect(() => {
    if (fieldDefinitions && config.dataSource) {
      const fields: ReportField[] = fieldDefinitions.map((field: any, index: number) => ({
        id: `${config.dataSource}-${field.name}-${index}`,
        name: field.name,
        label: field.label,
        type: field.type as 'string' | 'number' | 'date' | 'boolean',
        selected: false,
        aggregation: 'none' as AggregationType,
      }))
      dispatch({ type: 'SET_FIELDS', fields })
    }
  }, [fieldDefinitions, config.dataSource])

  // Apply template field selections when field definitions load
  useEffect(() => {
    if (selectedTemplate && fieldDefinitions && config.dataSource) {
      const template = REPORT_TEMPLATES.find((t: ReportTemplate) => t.id === selectedTemplate)
      if (template && template.dataSource === config.dataSource) {
        const templateFieldNames = template.fields.map((f: { name: string }) => f.name)
        const fields: ReportField[] = fieldDefinitions.map((field: any, index: number) => {
          const templateField = template.fields.find(
            (tf: { name: string; aggregation: AggregationType }) => tf.name === field.name
          )
          return {
            id: `${config.dataSource}-${field.name}-${index}`,
            name: field.name,
            label: field.label,
            type: field.type as 'string' | 'number' | 'date' | 'boolean',
            selected: templateFieldNames.includes(field.name),
            aggregation: templateField?.aggregation || 'none',
          }
        })
        dispatch({ type: 'SET_FIELDS', fields })
        dispatch({ type: 'SET_GROUP_BY', groupBy: template.groupBy })
        if (template.dateGrouping) {
          dispatch({ type: 'SET_DATE_GROUPING', dateGrouping: template.dateGrouping })
        }
      }
    }
  }, [selectedTemplate, fieldDefinitions, config.dataSource])

  // When the ?id= changes (navigating from one saved report to another while the
  // builder stays mounted), allow the load effect to re-apply the new report.
  useEffect(() => {
    reportConfigApplied.current = false
  }, [urlReportId])

  // Load a saved report (?id=) into the builder — once, when it arrives.
  // Sets everything except the field selections, which need the field palette
  // to load first (reconciled in the effect below).
  useEffect(() => {
    if (!loadedReport || reportConfigApplied.current) return
    reportConfigApplied.current = true
    const cfg = (loadedReport.config || {}) as Partial<ReportConfig> & {
      fields?: Array<string | { name: string; aggregation?: AggregationType }>
      selectedDateRange?: string
      currentDateField?: string
      customStartDate?: string
      customEndDate?: string
      comparisonEnabled?: boolean
      comparisonPeriod?: 'previous' | 'year_ago'
      activeQuickFilters?: string[]
    }
    setEditingReportId(loadedReport.id)
    setSelectedTemplate(null)
    setShowBuilderForm(true)
    // Restore the builder UI state that lives outside the reducer config so the
    // reopened report matches what was saved (date preset, custom range,
    // period comparison, and active quick-filter highlights).
    setSelectedDateRange(cfg.selectedDateRange || '')
    setCurrentDateField(cfg.currentDateField || '')
    setCustomStartDate(cfg.customStartDate || '')
    setCustomEndDate(cfg.customEndDate || '')
    setComparisonEnabled(cfg.comparisonEnabled || false)
    setComparisonPeriod(cfg.comparisonPeriod || 'previous')
    setActiveQuickFilters(cfg.activeQuickFilters || [])
    pendingReportFields.current = {
      dataSource: loadedReport.data_source,
      fields: cfg.fields || [],
    }
    dispatch({
      type: 'APPLY_TEMPLATE',
      config: {
        name: loadedReport.name,
        dataSource: loadedReport.data_source,
        chartType: cfg.chartType || 'table',
        groupBy: cfg.groupBy,
        dateGrouping: cfg.dateGrouping,
        sortBy: cfg.sortBy,
        sortOrder: cfg.sortOrder || 'asc',
        limit: cfg.limit ?? 10000,
        fields: [],
        filters: (cfg.filters || []).map((f, i) => ({
          id: `filter-${Date.now()}-${i}`,
          field: f.field,
          operator: f.operator,
          value: f.value,
        })),
      },
    })
    setFieldsOpen(true)
    setFiltersOpen(true)
    setVisualizationOpen(true)
  }, [loadedReport])

  // Reconcile a loaded report's saved field names/aggregations against the
  // fetched field palette (mirrors the template field-selection effect above).
  useEffect(() => {
    const pending = pendingReportFields.current
    if (!pending || !fieldDefinitions || config.dataSource !== pending.dataSource) return
    const selectedNames = new Set<string>()
    const aggByName = new Map<string, AggregationType>()
    pending.fields.forEach((f) => {
      if (typeof f === 'string') {
        selectedNames.add(f)
      } else {
        selectedNames.add(f.name)
        if (f.aggregation) aggByName.set(f.name, f.aggregation)
      }
    })
    const fields: ReportField[] = fieldDefinitions.map((field: any, index: number) => ({
      id: `${config.dataSource}-${field.name}-${index}`,
      name: field.name,
      label: field.label,
      type: field.type as 'string' | 'number' | 'date' | 'boolean',
      selected: selectedNames.has(field.name),
      aggregation: aggByName.get(field.name) || ('none' as AggregationType),
    }))
    dispatch({ type: 'SET_FIELDS', fields })
    pendingReportFields.current = null
    // loadedReport is included so reconciliation still runs when a saved report's
    // data source matches the one already loaded (fieldDefinitions/dataSource
    // unchanged) — otherwise the pending fields would never be applied.
  }, [fieldDefinitions, config.dataSource, loadedReport])

  // Keyboard shortcuts
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if ((e.ctrlKey || e.metaKey) && e.key === 'Enter') {
        e.preventDefault()
        if (config.dataSource && selectedFieldsCount > 0 && !executeReport.isPending) {
          handleRunReport()
        }
      }
      if ((e.ctrlKey || e.metaKey) && e.key === 's') {
        e.preventDefault()
        if (
          config.name &&
          config.dataSource &&
          !createReport.isPending &&
          !updateReport.isPending
        ) {
          handleSaveReport()
        }
      }
    }
    window.addEventListener('keydown', handleKeyDown)
    return () => window.removeEventListener('keydown', handleKeyDown)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [config, executeReport.isPending, createReport.isPending, updateReport.isPending])

  // Live preview
  useEffect(() => {
    if (!isLivePreviewEnabled) return
    if (!config.dataSource || selectedFieldsCount === 0) return
    if (executeReport.isPending) return

    const timer = setTimeout(() => {
      handleRunReport()
    }, 800)

    return () => clearTimeout(timer)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    config.dataSource,
    config.fields,
    config.filters,
    config.groupBy,
    config.dateGrouping,
    config.sortBy,
    config.sortOrder,
    config.limit,
    config.chartType,
    isLivePreviewEnabled,
    selectedFieldsCount,
  ])

  // Auto-open Filters section when user selects first field (one-time)
  useEffect(() => {
    if (selectedFieldsCount > 0 && !filtersAutoOpened.current) {
      setFiltersOpen(true)
      filtersAutoOpened.current = true
    }
  }, [selectedFieldsCount])

  // Auto-open Visualization section when aggregation is set
  useEffect(() => {
    if (hasAggregations) {
      setVisualizationOpen(true)
    }
  }, [hasAggregations])

  // Template auto-run: when template fields finish loading, auto-execute
  useEffect(() => {
    if (
      selectedTemplate &&
      selectedFieldsCount > 0 &&
      !reportResults &&
      !executeReport.isPending &&
      !templateAutoRunTriggered.current
    ) {
      templateAutoRunTriggered.current = true
      const timer = setTimeout(() => {
        handleRunReport()
      }, 300)
      return () => clearTimeout(timer)
    }
    if (!selectedTemplate) {
      templateAutoRunTriggered.current = false
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedTemplate, selectedFieldsCount, reportResults, executeReport.isPending])

  // Handlers
  const handleDataSourceChange = (dataSource: string) => {
    dispatch({ type: 'SET_DATA_SOURCE', dataSource })
    setReportResults(null)
    setSelectedTemplate(null)
    setSelectedDateRange('')
    setCurrentDateField('')
    setFieldSearch('')
    setActiveQuickFilters([])
  }

  const handleApplyTemplate = (template: ReportTemplate) => {
    setSelectedTemplate(template.id)
    setCurrentDateField(template.dateField || '')
    setShowBuilderForm(true)
    templateAutoRunTriggered.current = false
    dispatch({
      type: 'APPLY_TEMPLATE',
      config: {
        name: template.name,
        dataSource: template.dataSource,
        chartType: template.chartType,
        groupBy: template.groupBy,
        dateGrouping: template.dateGrouping,
        fields: [],
        filters:
          template.filters?.map((f, i) => ({
            id: `filter-${Date.now()}-${i}`,
            field: f.field,
            operator: f.operator,
            value: f.value,
          })) || [],
      },
    })
    setReportResults(null)
    setFieldsOpen(true)
    setFiltersOpen(true)
    setVisualizationOpen(true)
    toast({
      title: 'Template Applied',
      description: `"${template.name}" template loaded. Running report...`,
    })
  }

  // Auto-run report when loaded via URL template param
  useEffect(() => {
    if (
      urlTemplate &&
      !urlTemplateApplied.current &&
      fieldDefinitions &&
      config.dataSource === urlTemplate.dataSource
    ) {
      urlTemplateApplied.current = true
    }
  }, [fieldDefinitions, config.dataSource, urlTemplate])

  const handleClearAll = useCallback(() => {
    dispatch({ type: 'CLEAR_ALL' })
    setReportResults(null)
    setSelectedTemplate(null)
    setSelectedDateRange('')
    setCurrentDateField('')
    setFieldSearch('')
    setActiveQuickFilters([])
    toast({ title: 'Configuration Cleared', description: 'All selections have been reset.' })
  }, [toast])

  const handleSelectPopular = useCallback(() => {
    const popularFieldNames = POPULAR_FIELDS[config.dataSource] || []
    if (!popularFieldNames.length) return
    const fields = config.fields.map((f) => ({
      ...f,
      selected: popularFieldNames.includes(f.name) ? true : f.selected,
    }))
    dispatch({ type: 'SET_FIELDS', fields })
    toast({
      title: 'Popular Fields Selected',
      description: `Selected ${popularFieldNames.length} commonly used fields.`,
    })
  }, [config.dataSource, config.fields, toast])

  const buildFieldsToSend = () => {
    return config.fields
      .filter((f) => f.selected)
      .map((f) => {
        if (f.aggregation && f.aggregation !== 'none') {
          return { name: f.name, aggregation: f.aggregation }
        }
        return f.name
      })
  }

  const buildFiltersToSend = () => {
    return config.filters.map((f) => ({
      field: f.field,
      operator: f.operator,
      value: f.value,
    }))
  }

  const getSameMonthLastYear = () => {
    const now = new Date()
    const start = new Date(now.getFullYear() - 1, now.getMonth(), 1)
    const end = new Date(now.getFullYear() - 1, now.getMonth() + 1, 0)
    return { start: formatDate(start), end: formatDate(end) }
  }

  const getSameQuarterLastYear = () => {
    const now = new Date()
    const quarter = Math.floor(now.getMonth() / 3)
    const start = new Date(now.getFullYear() - 1, quarter * 3, 1)
    const end = new Date(now.getFullYear() - 1, quarter * 3 + 3, 0)
    return { start: formatDate(start), end: formatDate(end) }
  }

  const getComparisonDateRange = (currentPeriod: string, compType: 'previous' | 'year_ago') => {
    if (compType === 'year_ago') {
      if (currentPeriod === 'this_month') return getSameMonthLastYear()
      if (currentPeriod === 'this_quarter') return getSameQuarterLastYear()
      if (currentPeriod === 'ytd') {
        const now = new Date()
        const start = new Date(now.getFullYear() - 1, 0, 1)
        const end = new Date(now.getFullYear() - 1, now.getMonth(), now.getDate())
        return { start: formatDate(start), end: formatDate(end) }
      }
    } else {
      const preset = DATE_RANGE_PRESETS.find((p) => {
        if (currentPeriod === 'this_month') return p.value === 'last_month'
        if (currentPeriod === 'this_quarter') return p.value === 'last_quarter'
        if (currentPeriod === 'ytd') return p.value === 'last_year'
        return false
      })
      if (preset) return preset.getRange()
    }
    return null
  }

  const handleRunReport = async () => {
    try {
      const fieldsToSend = buildFieldsToSend()
      const filters = buildFiltersToSend()

      const result = await executeReport.mutateAsync({
        dataSource: config.dataSource,
        config: {
          fields: fieldsToSend,
          filters,
          groupBy: config.groupBy || undefined,
          dateGrouping: config.dateGrouping || undefined,
          sortBy: config.sortBy,
          sortOrder: config.sortOrder,
          limit: config.limit,
        },
      } as any)

      const resultData = result?.data || result?.results || result
      setReportResults({ data: Array.isArray(resultData) ? resultData : [] })

      // Comparison query
      if (comparisonEnabled && selectedDateRange && currentDateField) {
        const compRange = getComparisonDateRange(selectedDateRange, comparisonPeriod)
        if (compRange) {
          const compFilters = filters.filter(
            (f) =>
              !(
                f.field === currentDateField &&
                (f.operator === 'greater_or_equal' || f.operator === 'less_or_equal')
              )
          )
          compFilters.push(
            { field: currentDateField, operator: 'greater_or_equal', value: compRange.start },
            { field: currentDateField, operator: 'less_or_equal', value: compRange.end }
          )
          try {
            const compResult = await executeReport.mutateAsync({
              dataSource: config.dataSource,
              config: {
                fields: fieldsToSend,
                filters: compFilters,
                groupBy: config.groupBy || undefined,
                dateGrouping: config.dateGrouping || undefined,
                sortBy: config.sortBy,
                sortOrder: config.sortOrder,
                limit: config.limit,
              },
            } as any)
            const compResultData = compResult?.data || compResult?.results || compResult
            setComparisonResults({
              data: Array.isArray(compResultData) ? compResultData : [],
              period: comparisonPeriod === 'previous' ? 'Previous Period' : 'Same Period Last Year',
              dateRange: compRange,
            })
          } catch {
            setComparisonResults(null)
          }
        }
      } else {
        setComparisonResults(null)
      }

      toast({
        title: 'Report Executed',
        description: `Successfully generated report with ${Array.isArray(resultData) ? resultData.length : 0} rows.${comparisonEnabled ? ' (with comparison)' : ''}`,
      })
    } catch (error) {
      toast({
        title: 'Error',
        description: explainError(error, 'Failed to execute report'),
        variant: 'destructive',
      })
    }
  }

  const handleSaveReport = async () => {
    if (!config.name.trim()) {
      toast({
        title: 'Validation Error',
        description: 'Please enter a report name.',
        variant: 'destructive',
      })
      return
    }
    const payload = {
      name: config.name,
      dataSource: config.dataSource,
      config: {
        fields: buildFieldsToSend(),
        filters: buildFiltersToSend(),
        groupBy: config.groupBy,
        dateGrouping: config.dateGrouping,
        sortBy: config.sortBy,
        sortOrder: config.sortOrder,
        chartType: config.chartType,
        limit: config.limit,
        // Persist the builder UI state that lives outside the reducer config so
        // a reopened report restores the date preset, custom range, period
        // comparison, and active quick-filter highlights (see load effect).
        selectedDateRange: selectedDateRange || null,
        currentDateField: currentDateField || null,
        customStartDate: customStartDate || null,
        customEndDate: customEndDate || null,
        comparisonEnabled,
        comparisonPeriod,
        activeQuickFilters,
      },
    }
    try {
      if (editingReportId) {
        await updateReport.mutateAsync({ id: editingReportId, data: payload as any })
        toast({
          title: 'Report Updated',
          description: `Report "${config.name}" has been updated.`,
        })
      } else {
        const created = await createReport.mutateAsync(payload as any)
        // Switch to edit mode so subsequent saves update this report, not duplicate it.
        if (created?.id) setEditingReportId(created.id)
        toast({
          title: 'Report Saved',
          description: `Report "${config.name}" has been saved successfully.`,
        })
      }
    } catch (error) {
      toast({
        title: 'Error',
        description: explainError(error, 'Failed to save report'),
        variant: 'destructive',
      })
    }
  }

  const handleExportCSV = () => {
    if (!reportResults?.data || reportResults.data.length === 0) {
      toast({
        title: 'No Data',
        description: 'Please run the report first before exporting.',
        variant: 'destructive',
      })
      return
    }
    try {
      exportToCSV({ data: reportResults.data, filename: `${config.name || 'custom-report'}.csv` })
      toast({ title: 'Export Successful', description: 'Report has been exported to CSV.' })
    } catch (error) {
      toast({
        title: 'Error',
        description: explainError(error, 'Failed to export report'),
        variant: 'destructive',
      })
    }
  }

  const handleExportPdf = async () => {
    try {
      if (selectedFieldsCount === 0 || !config.dataSource) {
        toast({
          title: 'Cannot Export',
          description: 'Please select fields and run the report first.',
          variant: 'destructive',
        })
        return
      }
      const pdfBlob = await exportPdf.mutateAsync({
        dataSource: config.dataSource,
        config: {
          fields: buildFieldsToSend(),
          filters: buildFiltersToSend(),
          groupBy: config.groupBy || undefined,
          dateGrouping: config.dateGrouping || undefined,
          sortBy: config.sortBy,
          sortOrder: config.sortOrder,
          limit: config.limit,
        },
        reportName: config.name || 'Custom Report',
      })
      const url = window.URL.createObjectURL(pdfBlob)
      const link = document.createElement('a')
      link.href = url
      link.download = `${config.name || 'custom-report'}.pdf`
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
      window.URL.revokeObjectURL(url)
      toast({ title: 'PDF Exported', description: 'Report has been exported to PDF.' })
    } catch (error) {
      toast({
        title: 'Error',
        description: explainError(error, 'Failed to export PDF'),
        variant: 'destructive',
      })
    }
  }

  const step1Complete = !!config.dataSource
  const step2Complete = selectedFieldsCount > 0
  const step3Complete = !!reportResults
  const currentStep = !step1Complete ? 1 : !step2Complete ? 2 : 3

  // Show welcome state when no data source and user hasn't clicked "Build from scratch"
  if (!config.dataSource && !showBuilderForm) {
    return (
      <WelcomeState
        onSelectTemplate={handleApplyTemplate}
        onBuildFromScratch={() => setShowBuilderForm(true)}
        templateGalleryOpen={templateGalleryOpen}
        onTemplateGalleryOpenChange={setTemplateGalleryOpen}
      />
    )
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Report Builder</h1>
          <p className="text-muted-foreground">
            Create custom reports with fields, filters, aggregations, and visualizations
          </p>
          <p className="text-xs text-muted-foreground mt-1">
            Keyboard shortcuts:{' '}
            <kbd className="px-1.5 py-0.5 bg-muted rounded text-xs font-mono">Ctrl+Enter</kbd> Run{' '}
            <kbd className="px-1.5 py-0.5 bg-muted rounded text-xs font-mono">Ctrl+S</kbd> Save
          </p>
        </div>
        <div className="flex gap-2">
          <TemplateGallery
            open={templateGalleryOpen}
            onOpenChange={setTemplateGalleryOpen}
            onSelectTemplate={handleApplyTemplate}
          />
          <Button
            variant="ghost"
            onClick={handleClearAll}
            disabled={!config.dataSource}
            aria-label="Clear all selections"
            title="Clear all selections"
          >
            <RotateCcw className="mr-2 h-4 w-4" aria-hidden="true" />
            Clear
          </Button>
          <Button
            variant="outline"
            onClick={handleExportCSV}
            disabled={!reportResults?.data || reportResults.data.length === 0}
            aria-label="Export report to CSV"
          >
            <Download className="mr-2 h-4 w-4" aria-hidden="true" />
            CSV
          </Button>
          <Button
            variant="outline"
            onClick={handleExportPdf}
            disabled={!config.dataSource || selectedFieldsCount === 0 || exportPdf.isPending}
            aria-label="Export report to PDF"
          >
            {exportPdf.isPending ? (
              <Loader2 className="mr-2 h-4 w-4 animate-spin" aria-hidden="true" />
            ) : (
              <FileDown className="mr-2 h-4 w-4" aria-hidden="true" />
            )}
            PDF
          </Button>
          <Button
            variant="outline"
            onClick={handleSaveReport}
            disabled={
              !config.name || !config.dataSource || createReport.isPending || updateReport.isPending
            }
            aria-label={
              editingReportId
                ? 'Update saved report (Ctrl+S)'
                : 'Save report configuration (Ctrl+S)'
            }
            title={editingReportId ? 'Update report (Ctrl+S)' : 'Save report (Ctrl+S)'}
          >
            <Save className="mr-2 h-4 w-4" aria-hidden="true" />
            {createReport.isPending || updateReport.isPending
              ? 'Saving...'
              : editingReportId
                ? 'Update'
                : 'Save'}
          </Button>
          <Button
            onClick={handleRunReport}
            disabled={!config.dataSource || selectedFieldsCount === 0 || executeReport.isPending}
            aria-label="Run report (Ctrl+Enter)"
            title="Run report (Ctrl+Enter)"
          >
            {executeReport.isPending ? (
              <Loader2 className="mr-2 h-4 w-4 animate-spin" aria-hidden="true" />
            ) : (
              <Play className="mr-2 h-4 w-4" aria-hidden="true" />
            )}
            {executeReport.isPending ? 'Running...' : 'Run Report'}
          </Button>
        </div>
      </div>

      {/* Contextual hint when buttons are disabled */}
      {(!config.dataSource || selectedFieldsCount === 0) && (
        <p className="text-sm text-muted-foreground text-right -mt-4" aria-live="polite">
          {!config.dataSource
            ? 'Select a data source to get started'
            : 'Select at least one field to run the report'}
        </p>
      )}

      {/* Step Progress Indicator */}
      <StepIndicator
        currentStep={currentStep}
        step1Complete={step1Complete}
        step2Complete={step2Complete}
        step3Complete={step3Complete}
        onStepClick={(step) => {
          if (step === 1) setDataSourceOpen(true)
          if (step === 2) setFieldsOpen(true)
          if (step === 3 && step1Complete && step2Complete && !executeReport.isPending)
            handleRunReport()
        }}
      />

      {/* Report Name */}
      <Card>
        <CardHeader>
          <CardTitle>Report Details</CardTitle>
          <CardDescription>Give your report a name and description</CardDescription>
        </CardHeader>
        <CardContent>
          <div>
            <Label htmlFor="report-name">Report Name *</Label>
            <Input
              id="report-name"
              placeholder="e.g., Monthly Sales Report"
              value={config.name}
              onChange={(e) => dispatch({ type: 'SET_NAME', name: e.target.value })}
              aria-required="true"
            />
          </div>
        </CardContent>
      </Card>

      {/* Date Range + Filters (above two-column layout) */}
      <FilterBuilder
        dataSource={config.dataSource}
        fields={config.fields}
        filters={config.filters}
        currentDateField={currentDateField}
        selectedDateRange={selectedDateRange}
        customStartDate={customStartDate}
        customEndDate={customEndDate}
        comparisonEnabled={comparisonEnabled}
        comparisonPeriod={comparisonPeriod}
        activeQuickFilters={activeQuickFilters}
        open={filtersOpen}
        onOpenChange={setFiltersOpen}
        dispatch={dispatch}
        onDateFieldChange={setCurrentDateField}
        onDateRangeChange={setSelectedDateRange}
        onCustomStartDateChange={setCustomStartDate}
        onCustomEndDateChange={setCustomEndDate}
        onComparisonEnabledChange={setComparisonEnabled}
        onComparisonPeriodChange={setComparisonPeriod}
        onActiveQuickFiltersChange={setActiveQuickFilters}
      />

      {/* Two-Column Layout */}
      <div className="grid grid-cols-1 lg:grid-cols-[1fr_2fr] gap-6">
        {/* Left: Configuration */}
        <div className="space-y-4">
          <EntitySelector
            dataSource={config.dataSource}
            fieldCount={config.fields.length}
            fieldsLoading={fieldsLoading}
            open={dataSourceOpen}
            onOpenChange={setDataSourceOpen}
            onDataSourceChange={handleDataSourceChange}
          />

          <FieldPalette
            dataSource={config.dataSource}
            fields={config.fields}
            fieldSearch={fieldSearch}
            selectedCount={selectedFieldsCount}
            fieldsLoading={fieldsLoading}
            open={fieldsOpen}
            onOpenChange={setFieldsOpen}
            onFieldSearchChange={setFieldSearch}
            onToggleField={(id) => dispatch({ type: 'TOGGLE_FIELD', fieldId: id })}
            onAggregationChange={(id, agg) =>
              dispatch({ type: 'SET_FIELD_AGGREGATION', fieldId: id, aggregation: agg })
            }
            onSelectPopular={handleSelectPopular}
          />

          <GroupingConfig
            dataSource={config.dataSource}
            fields={config.fields}
            groupBy={config.groupBy}
            dateGrouping={config.dateGrouping}
            sortBy={config.sortBy}
            sortOrder={config.sortOrder}
            chartType={config.chartType}
            limit={config.limit}
            hasAggregations={hasAggregations}
            open={visualizationOpen}
            onOpenChange={setVisualizationOpen}
            onGroupByChange={(gb) => dispatch({ type: 'SET_GROUP_BY', groupBy: gb })}
            onDateGroupingChange={(dg) => dispatch({ type: 'SET_DATE_GROUPING', dateGrouping: dg })}
            onSortByChange={(sb) =>
              dispatch({ type: 'SET_SORT', sortBy: sb, sortOrder: config.sortOrder })
            }
            onSortOrderChange={(so) =>
              dispatch({ type: 'SET_SORT', sortBy: config.sortBy, sortOrder: so })
            }
            onChartTypeChange={(ct) => dispatch({ type: 'SET_CHART_TYPE', chartType: ct })}
            onLimitChange={(l) => dispatch({ type: 'SET_LIMIT', limit: l })}
          />
        </div>

        {/* Right: Live Preview */}
        <ReportPreview
          chartType={config.chartType}
          reportResults={reportResults}
          comparisonResults={comparisonResults}
          isExecuting={executeReport.isPending}
          isLivePreviewEnabled={isLivePreviewEnabled}
          onLivePreviewChange={setIsLivePreviewEnabled}
        />
      </div>
    </div>
  )
}

// --- Inline helper components ---

const CHART_ICONS: Record<ChartType, typeof BarChart3> = {
  bar: BarChart3,
  line: LineChartIcon,
  area: TrendingUp,
  pie: PieChart,
  table: Table2,
}

function WelcomeState({
  onSelectTemplate,
  onBuildFromScratch,
  templateGalleryOpen,
  onTemplateGalleryOpenChange,
}: {
  onSelectTemplate: (template: ReportTemplate) => void
  onBuildFromScratch: () => void
  templateGalleryOpen: boolean
  onTemplateGalleryOpenChange: (open: boolean) => void
}) {
  const featuredTemplates = REPORT_TEMPLATES.filter((t) => FEATURED_TEMPLATE_IDS.includes(t.id))

  return (
    <div className="space-y-8">
      <div className="text-center py-8">
        <h1 className="text-3xl font-bold tracking-tight mb-2">Report Builder</h1>
        <p className="text-lg text-muted-foreground max-w-xl mx-auto">
          Choose a template to get started quickly, or build a report from scratch.
        </p>
      </div>

      {/* Featured Templates */}
      <div>
        <h2 className="text-sm font-semibold text-muted-foreground uppercase tracking-wider mb-4">
          Popular Templates
        </h2>
        <div
          className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4"
          role="group"
          aria-label="Featured report templates"
        >
          {featuredTemplates.map((template) => {
            const Icon = CHART_ICONS[template.chartType]
            return (
              <Card
                key={template.id}
                className="cursor-pointer hover:border-primary transition-colors"
                role="button"
                tabIndex={0}
                aria-label={`Use ${template.name} template`}
                onClick={() => onSelectTemplate(template)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault()
                    onSelectTemplate(template)
                  }
                }}
              >
                <CardContent className="p-5">
                  <div className="flex items-start gap-3">
                    <div className="p-2 bg-primary/10 rounded-md">
                      <Icon className="h-5 w-5 text-primary" aria-hidden="true" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        <span className="text-sm font-medium">{template.name}</span>
                        <Badge variant="outline" className="text-xs capitalize shrink-0">
                          {template.chartType}
                        </Badge>
                      </div>
                      <p className="text-xs text-muted-foreground">{template.description}</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            )
          })}
        </div>
        <div className="mt-4 text-center">
          <TemplateGallery
            open={templateGalleryOpen}
            onOpenChange={onTemplateGalleryOpenChange}
            onSelectTemplate={onSelectTemplate}
          />
        </div>
      </div>

      {/* Divider */}
      <div className="flex items-center gap-4">
        <div className="flex-1 h-px bg-border" />
        <span className="text-sm text-muted-foreground">or</span>
        <div className="flex-1 h-px bg-border" />
      </div>

      {/* Build from scratch */}
      <div className="text-center">
        <Card
          className="inline-block cursor-pointer hover:border-primary transition-colors max-w-md"
          role="button"
          tabIndex={0}
          aria-label="Build a report from scratch"
          onClick={onBuildFromScratch}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault()
              onBuildFromScratch()
            }
          }}
        >
          <CardContent className="p-6 flex items-center gap-4">
            <div className="p-3 bg-muted rounded-md">
              <Database className="h-6 w-6 text-muted-foreground" aria-hidden="true" />
            </div>
            <div className="text-left">
              <p className="font-medium">Build from scratch</p>
              <p className="text-sm text-muted-foreground">
                Choose a data source, pick fields, add filters, and configure visualizations
              </p>
            </div>
            <ArrowRight className="h-5 w-5 text-muted-foreground shrink-0" aria-hidden="true" />
          </CardContent>
        </Card>
      </div>
    </div>
  )
}

function StepIndicator({
  currentStep,
  step1Complete,
  step2Complete,
  step3Complete,
  onStepClick,
}: {
  currentStep: number
  step1Complete: boolean
  step2Complete: boolean
  step3Complete: boolean
  onStepClick: (step: number) => void
}) {
  const steps = [
    { label: 'Data Source', complete: step1Complete },
    { label: 'Fields', complete: step2Complete },
    { label: 'Run Report', complete: step3Complete },
  ]

  return (
    <nav aria-label="Report building progress" className="flex items-center justify-center gap-0">
      {steps.map((step, index) => {
        const stepNum = index + 1
        const isActive = stepNum === currentStep
        const isClickable =
          stepNum === 1 || (stepNum === 2 && step1Complete) || (stepNum === 3 && step2Complete)

        return (
          <div key={step.label} className="flex items-center">
            <button
              className={`flex items-center gap-2 px-3 py-2 rounded-lg transition-colors text-sm ${
                step.complete
                  ? 'text-primary font-medium'
                  : isActive
                    ? 'text-foreground font-medium bg-muted'
                    : 'text-muted-foreground'
              } ${isClickable ? 'cursor-pointer hover:bg-muted/80' : 'cursor-default'}`}
              onClick={() => isClickable && onStepClick(stepNum)}
              aria-current={isActive ? 'step' : undefined}
              aria-label={`Step ${stepNum}: ${step.label}${step.complete ? ' (completed)' : ''}`}
              tabIndex={isClickable ? 0 : -1}
              aria-disabled={!isClickable}
            >
              <div
                className={`flex items-center justify-center h-6 w-6 rounded-full text-xs font-bold ${
                  step.complete
                    ? 'bg-primary text-primary-foreground'
                    : isActive
                      ? 'bg-foreground text-background'
                      : 'bg-muted text-muted-foreground'
                }`}
              >
                {step.complete ? <Check className="h-3.5 w-3.5" aria-hidden="true" /> : stepNum}
              </div>
              <span className="hidden sm:inline">{step.label}</span>
            </button>
            {index < steps.length - 1 && (
              <div
                className={`w-8 h-px mx-1 ${step.complete ? 'bg-primary' : 'bg-border'}`}
                aria-hidden="true"
              />
            )}
          </div>
        )
      })}
    </nav>
  )
}
